import { ReactNode } from 'react';
import { TFunction } from 'react-i18next';
import { IUtmTouchBlock, IOrderDetails, IPaymentsInfo, OrdersDeliveryType, PaymentTypes } from '@/models/orders.model';
import { getDeliveryTypeData, getDepartmentName } from '../helpers';
import { formatUtmSource } from './formatUtmSource';
import { DeliveryDetailsBlock } from '../components';
import { toFixedDecimal, toNumber } from '@/utils';

export const getOrderClientData = (t: TFunction, order: IOrderDetails | null) => {
  return [
    {
      label: t('order_details_full_name'),
      value: order?.contact_name,
    },
    {
      label: t('order_details_phone'),
      value: order?.contact_phone,
    },
    {
      label: t('order_details_email'),
      value: order?.contact_email,
    },
    {
      label: t('order_details_client_comment'),
      value: order?.description,
    },
  ];
};

export const getOrderDeliveryData = (t: TFunction, order: IOrderDetails | null, settlementName?: string) => {
  return [
    {
      label: t('order_details_delivery_type'),
      value: t(getDeliveryTypeData(order?.delivery?.delivery)),
    },
    {
      label: t('order_details_delivery_settlement'),
      value: settlementName ?? '-',
    },
    {
      label: t('order_details_delivery_address'),
      value:
        order?.delivery?.delivery === OrdersDeliveryType.NOVA_POSHTA
          ? getDepartmentName(order?.delivery?.address ?? '')
          : order?.delivery?.address ?? '',
    },
    ...(order?.delivery?.delivery === OrdersDeliveryType.GOST
      ? [
          {
            label: t('order_details_delivery_details'),
            value: order && <DeliveryDetailsBlock deliveryData={order.delivery} />,
          },
        ]
      : []),
    {
      label: t('order_details_delivery_comment'),
      value: order?.delivery?.comment,
    },
  ];
};

const isAfterCallPaymentType = (paymentType?: PaymentTypes) =>
  paymentType === PaymentTypes.PREPAYMENT_AFTER_CALL || paymentType === PaymentTypes.PAYMENT_AFTER_CALL;

export const getOrderPaymentData = (t: TFunction, order: IPaymentsInfo | null, paymentType?: PaymentTypes) => {
  const isWithMinPrepayment = isAfterCallPaymentType(paymentType) && !!order?.prepayment && order.prepayment > 0;
  const isWithPrepaymentBalance = !!order?.prepaymentBalance && order?.prepaymentBalance > 0;
  const isWithPaidAmount = !!order?.paidAmount;
  const isWithPaymentBalance = !!order?.paymentBalance && order?.paymentBalance > 0;
  const isWithRefundAmount = !!order?.refundAmount && order?.refundAmount > 0;
  const receiptUrl = order?.transactions?.find((tx) => tx.receipt_url)?.receipt_url ?? null;
  return [
    {
      label: t('orders_legal_type'),
      value: t(`orders_${order?.legalType}`),
    },
    {
      label: t('order_details_prepayment'),
      value: isWithMinPrepayment ? `${order?.prepayment} ₴` : null,
      tooltip: t('orders_list_prepayment_tooltip'),
    },
    {
      label: t('orders_list_prepayment_balance'),
      value: isWithPrepaymentBalance ? `${order?.prepaymentBalance} ₴` : null,
      tooltip: t('orders_list_prepayment_balance_tooltip'),
    },
    {
      label: t('orders_list_payed_amount'),
      value: isWithPaidAmount ? `${order?.paidAmount} ₴` : null,
      tooltip: t('orders_list_payed_amount_tooltip'),
    },
    {
      label: t('orders_list_balance_payment'),
      value: isWithPaymentBalance ? `${order?.paymentBalance} ₴` : null,
      tooltip: t('orders_list_balance_payment_tooltip'),
    },
    {
      label: t('orders_list_returning_sum'),
      value: isWithRefundAmount ? `${order?.refundAmount} ₴` : null,
      tooltip: t('orders_list_returning_sum_tooltip'),
    },
    {
      label: t('order_receipt_url'),
      value: receiptUrl ? (
        <a href={receiptUrl} target="_blank" rel="noopener noreferrer">
          {t('order_receipt_url_link')}
        </a>
      ) : null,
    },
  ];
};

const formatUtmTouchBlock = (
  t: TFunction,
  touch: IUtmTouchBlock | null | undefined,
  fallbackUtmSource?: string | null,
): ReactNode => {
  if (!touch) return formatUtmSource(t, fallbackUtmSource) ?? '—';
  const lines = (['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const)
    .map((k) => {
      if (!touch[k]) return null;
      const value = k === 'utm_source' ? formatUtmSource(t, touch[k]) : touch[k];
      return value ? `${k}=${value}` : null;
    })
    .filter(Boolean) as string[];
  if (!lines.length) return formatUtmSource(t, fallbackUtmSource) ?? '—';
  return (
    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 'inherit' }}>
      {lines.join('\n')}
    </pre>
  );
};

export const getOrderUtmAttributionData = (
  t: TFunction,
  order: IOrderDetails | null,
): { label: string; value: ReactNode }[] => {
  return [
    {
      label: t('order_utm_first_touch'),
      value: formatUtmTouchBlock(t, order?.utm_first_touch ?? undefined, order?.utm_source),
    },
    {
      label: t('order_utm_last_touch'),
      value: formatUtmTouchBlock(t, order?.utm_last_touch ?? undefined),
    },
  ];
};

export const getOrderResultsData = (t: TFunction, order: IOrderDetails | null) => {
  const totalSum = toNumber(order?.total_price) + (order?.total_discount ?? 0) - toNumber(order?.delivery_price);
  return [
    {
      label: t('orders_list_total_sum'),
      value: `${toFixedDecimal(totalSum)} ₴`,
    },
    {
      label: t('orders_list_discount'),
      value: `${order?.total_discount} ₴`,
    },
    {
      label: t('orders_list_delivery'),
      value:
        order?.delivery?.delivery === OrdersDeliveryType.NOVA_POSHTA
          ? t('orders_list_delivery_carriers_rates')
          : `${order?.delivery_price} ₴`,
    },
    {
      label: t('orders_list_payment_amount'),
      value: `${toNumber(order?.total_price)} ₴`,
    },
  ];
};
